import { Yii } from "../../../libraries/interfaces/Yii";

const OAUTH_PATH: string = '/oauth/token';
const SAM_PATH: string = '/api/sam/token';
const LOGOUT_PATH: string = '/api/logout';
const REGISTER_PATH: string = '/register';
const PROFILE_PATH: string = '/profile';
const FORGOT_PASSWORD_PATH: string = '/forgot-password';
const API_ERROR_CODE_INVALID_GRANT: string = 'invalid_credentials';
const API_ERROR_CODE_INACTIVE_USER: string = 'user_inactive';
const API_ERROR_CODE_DEFAULT: string = 'default_error';
const API_ERROR_CODE: string[] = [API_ERROR_CODE_INVALID_GRANT, API_ERROR_CODE_INACTIVE_USER];
const DEFAULT_ACCESS_TOKEN_TYPE: string = 'Bearer';

class Wording {
    public readonly defaultErrorMessage: string = Yii.t('wui-app-js', 'Une erreur est survenue');
    public readonly invalidGrantErrorMessage: string = Yii.t('wui-app-js', 'Identifiant ou mot de passe incorrect');
    public readonly inactiveUserErrorMessage: string = Yii.t('wui-app-js', 'Le compte est inactif');
    public readonly missingConfigErrorMessage: string = Yii.t('wui-app-js', 'Le lien vers Afidium Connect est manquant');
    public readonly invalidConfigErrorMessage: string = Yii.t('wui-app-js', 'Une erreur est présente dans la configuration');
}

export interface IAuthForm {
    login: {
        uid: string;
        pwd: string;
    };
}
export interface IAfidiumConnectOAuthRequest {
    client_id: string;
    client_secret: string;
    username: string;
    password: string;
    grant_type: 'password';
}

export interface IAfidiumConnectOAuthResponse {
    token_type: string;
    expires_in?: number;
    access_token: string;
    refresh_token?: string;
}

export interface IAfidiumConnectSamResponse {
    error: boolean;
    warning: boolean;
    code: number;
    message: string;
    data: string;
}

export interface IAfidiumConnectAuth {
    username: string;
    password: string;
}

export interface IAfidiumConnectConfig {
    clientId: string;
    clientSecret: string;
    url: string;
    bypass: boolean;
    hideLoginLinks: boolean;
}

export interface IAfidiumConnectLink {
    createAccountLink: string;
    forgotPasswordLink: string;
}

export class AfidiumConnect {
    public apiErrorMessage: string;
    public apiErrorCode: string;
    private readonly clientId: string;
    private readonly clientSecret: string;
    private readonly url: string;
    private auth: IAfidiumConnectAuth = null;
    private oAuthResponse: IAfidiumConnectOAuthResponse = null;
    private samResponse: IAfidiumConnectSamResponse = null;
    private readonly wording: Wording = new Wording();

    public constructor(afidiumConnectConfig: IAfidiumConnectConfig) {
        this.clientId = afidiumConnectConfig.clientId && afidiumConnectConfig.clientId !== '' ? afidiumConnectConfig.clientId : null;
        this.clientSecret = afidiumConnectConfig.clientSecret && afidiumConnectConfig.clientSecret !== '' ? afidiumConnectConfig.clientSecret : null;
        this.url = afidiumConnectConfig.url && afidiumConnectConfig.url !== '' ? afidiumConnectConfig.url : null;
    }

    public isConfigValid(): boolean {
        return Boolean(this.clientId) && Boolean(this.clientSecret) && Boolean(this.url);
    }

    public getInvalidConfigMessage(): string {
        if (this.clientId === null && this.clientSecret === null) {
            return this.wording.invalidConfigErrorMessage;
        }
        if (this.url === null) {
            return this.wording.missingConfigErrorMessage;
        }
        return this.wording.defaultErrorMessage;
    }

    public getOAuthUrl(): string {
        return this.url + OAUTH_PATH;
    }

    public getSamUrl(): string {
        return this.url + SAM_PATH;
    }

    public getLogoutUrl(): string {
        return this.url + LOGOUT_PATH;
    }

    public setAuth(authForm: IAuthForm): void {
        this.auth = authForm.login && authForm.login.uid && authForm.login.pwd ? {
            username: authForm.login.uid,
            password: authForm.login.pwd
        } : null;
    }

    public isAuthValid(): boolean {
        return Boolean(this.auth);
    }

    public getOAuthRequest(): IAfidiumConnectOAuthRequest {
        return {
            client_id: this.clientId,
            client_secret: this.clientSecret,
            username: this.auth.username,
            password: this.auth.password,
            grant_type: 'password'
        };
    }

    public setOAuthResponse(afidiumConnectOAuthResponse: IAfidiumConnectOAuthResponse): void {
        this.oAuthResponse = {
            token_type: afidiumConnectOAuthResponse.token_type ?? DEFAULT_ACCESS_TOKEN_TYPE,
            access_token: afidiumConnectOAuthResponse.access_token ?? null,
            expires_in: afidiumConnectOAuthResponse.expires_in ?? null,
            refresh_token: afidiumConnectOAuthResponse.refresh_token ?? null
        };
    }

    public isOAuthResponseValid(): boolean {
        return Boolean(this.oAuthResponse?.access_token) && Boolean(this.oAuthResponse?.token_type);
    }

    public setSamResponse(afidiumConnectSamResponse: IAfidiumConnectSamResponse): void {
        this.samResponse = {
            code: afidiumConnectSamResponse.code ?? null,
            data: afidiumConnectSamResponse.data ?? null,
            error: afidiumConnectSamResponse.error ?? null,
            message: afidiumConnectSamResponse.message ?? null,
            warning: afidiumConnectSamResponse.warning ?? null
        };
    }

    public isSamResponseValid(): boolean {
        return this.samResponse?.code === 200 && Boolean(this.samResponse?.data);
    }

    public getSamAuthorizationHeader(): string {
        return `${this.oAuthResponse.token_type} ${this.oAuthResponse.access_token}`;
    }

    public getSamToken(): string {
        return this.samResponse.data;
    }

    public getOAuthToken(): string {
        return this.oAuthResponse.access_token;
    }

    public getOAuthTokenType(): string {
        return this.oAuthResponse.token_type;
    }

    public getCreateAccountLink(): string {
        return `${this.url}${REGISTER_PATH}?client_id=${this.clientId}`;
    }

    public getForgotPasswordLink(): string {
        return this.url + FORGOT_PASSWORD_PATH;
    }

    public getProfileLink(): string {
        return `${this.url}${PROFILE_PATH}?token=${this.oAuthResponse.access_token}&urlCallback=${document.URL}`;
    }

    public setApiError(errorCode?: string): void {
        this.setApiErrorCode(errorCode);
        this.setApiErrorMessage();
    }

    private setApiErrorCode(errorCode?: string): void {
        this.apiErrorCode = API_ERROR_CODE_DEFAULT;
        if (errorCode && API_ERROR_CODE.includes(errorCode)) {
            this.apiErrorCode = errorCode;
        }
    }

    private setApiErrorMessage(): void {
        this.apiErrorMessage = null;
        if (this.apiErrorCode) {
            switch(this.apiErrorCode) {
                case API_ERROR_CODE_INVALID_GRANT:
                    this.apiErrorMessage = this.wording.invalidGrantErrorMessage;
                    break;
                case API_ERROR_CODE_INACTIVE_USER:
                    this.apiErrorMessage = this.wording.inactiveUserErrorMessage;
                    break;
                default:
                    this.apiErrorMessage = this.wording.defaultErrorMessage;
            }
        }
    }
}
